import type { MarketAccumulator } from '../../types/apiReturnType'

/**
 * Builders for {@link MarketAccumulator}. All arithmetic is bigint → decimal
 * string; nothing here goes through float64, because the consumer divides two
 * of these ~1e-6 apart and the digits float64 drops are exactly the ones a
 * realized-return ratio depends on.
 */

/** bigint scaled by 10^decimals → plain decimal string, trailing zeros trimmed. */
export function scaledToDecimal(raw: bigint, decimals: number): string {
  const neg = raw < 0n
  const text = (neg ? -raw : raw).toString().padStart(decimals + 1, '0')
  const cut = text.length - decimals
  const frac = text.slice(cut).replace(/0+$/, '')
  const out = frac ? `${text.slice(0, cut)}.${frac}` : text.slice(0, cut)
  return neg ? `-${out}` : out
}

function toBigInt(v: unknown): bigint | undefined {
  if (v === null || v === undefined) return undefined
  try {
    const b = typeof v === 'bigint' ? v : BigInt(String(v))
    return b
  } catch {
    return undefined
  }
}

/** Aave-shaped RAY indices (1e27). */
export function rayAccumulator(
  liquidityIndex: unknown,
  variableBorrowIndex?: unknown,
): MarketAccumulator | undefined {
  const s = toBigInt(liquidityIndex)
  if (s === undefined || s <= 0n) return undefined
  const b = toBigInt(variableBorrowIndex)
  return {
    supplyIndex: scaledToDecimal(s, 27),
    ...(b !== undefined && b > 0n ? { borrowIndex: scaledToDecimal(b, 27) } : {}),
    kind: 'ray',
  }
}

/** Compound V2 `exchangeRateStored` (1e18-scaled, underlying per cToken). */
export function exchangeRateAccumulator(
  exchangeRate: unknown,
  borrowIndex?: unknown,
): MarketAccumulator | undefined {
  const s = toBigInt(exchangeRate)
  if (s === undefined || s <= 0n) return undefined
  const b = toBigInt(borrowIndex)
  return {
    supplyIndex: scaledToDecimal(s, 18),
    ...(b !== undefined && b > 0n ? { borrowIndex: scaledToDecimal(b, 18) } : {}),
    kind: 'exchange_rate',
  }
}

/** 30 decimal places — the `lending-owners` Morpho backfill's INDEX_DECIMALS,
 *  so a live row and a replayed row for the same market are the same number. */
const ASSETS_PER_SHARE_DECIMALS = 30

/** Morpho-shaped `assets / shares`, raw over raw. */
export function assetsPerShareAccumulator(
  supplyAssets: unknown,
  supplyShares: unknown,
  borrowAssets?: unknown,
  borrowShares?: unknown,
): MarketAccumulator | undefined {
  const ratio = (a: unknown, s: unknown): string | undefined => {
    const ab = toBigInt(a)
    const sb = toBigInt(s)
    if (ab === undefined || sb === undefined || sb <= 0n || ab < 0n) return undefined
    return scaledToDecimal(
      (ab * 10n ** BigInt(ASSETS_PER_SHARE_DECIMALS)) / sb,
      ASSETS_PER_SHARE_DECIMALS,
    )
  }
  const supplyIndex = ratio(supplyAssets, supplyShares)
  if (!supplyIndex) return undefined
  const borrowIndex = ratio(borrowAssets, borrowShares)
  return {
    supplyIndex,
    ...(borrowIndex ? { borrowIndex } : {}),
    kind: 'assets_per_share',
  }
}

/** ERC-4626-style `convertToAssets(10^shareDecimals)` in raw asset units. */
export function sharePriceAccumulator(
  assetsPerUnitShare: unknown,
): MarketAccumulator | undefined {
  const s = toBigInt(assetsPerUnitShare)
  if (s === undefined || s <= 0n) return undefined
  return { supplyIndex: s.toString(), kind: 'share_price' }
}
